import { useCallback, useEffect, useState } from "react";
import { RoomEvent, type LocalParticipant } from "livekit-client";
import { useRoomContext } from "@livekit/components-react";
import { classifyMediaError, type MediaErrorCause } from "../lib/deviceErrors";

export interface DeviceToggles {
  /** Non-null when the local camera could not be acquired — drives the
   *  persistent in-tile affordance. Cleared automatically on the next
   *  successful enable (live recovery, no rejoin). */
  cameraError: MediaErrorCause | null;
  /** Non-null when the local mic could not be acquired — drives the persistent
   *  inline affordance by the mic control (and the listen-only join state). */
  micError: MediaErrorCause | null;
  /** Enable/disable the camera. Resolves `true` on success. On an *enable*
   *  failure it classifies the cause into `cameraError` and resolves `false`
   *  (the button never flips to on). */
  toggleCamera: (next: boolean) => Promise<boolean>;
  /** Enable/disable the mic, symmetric to `toggleCamera`. */
  toggleMic: (next: boolean) => Promise<boolean>;
}

/**
 * Error-aware wrapper around LiveKit's camera/mic toggles (GH-1179).
 *
 * Two failure paths feed the same persistent affordances:
 *
 *  1. Join-time — `<LiveKitRoom audio/video>` auto-publishes on connect. When
 *     getUserMedia is denied (or no device is present) during that publish, no
 *     toggle handler runs; LiveKit emits `RoomEvent.MediaDevicesError` with the
 *     failed device `kind`. We classify it there so the join path shows the
 *     same message as a manual toggle (AC1/AC2).
 *
 *  2. Manual toggle — the user clicks the camera/mic control. The wrapper
 *     awaits `setCameraEnabled` / `setMicrophoneEnabled`, and on an enable
 *     failure classifies the DOMException (AC3/AC5).
 *
 * Recovery (AC4) is automatic and toggle-driven: any successful enable clears
 * the stored error and publishes the track — we deliberately do NOT poll
 * `navigator.permissions`, since a hard site-level block cannot be re-prompted
 * from the app anyway.
 */
export function useDeviceToggles(
  localParticipant: LocalParticipant | undefined | null,
): DeviceToggles {
  const room = useRoomContext();
  const [cameraError, setCameraError] = useState<MediaErrorCause | null>(null);
  const [micError, setMicError] = useState<MediaErrorCause | null>(null);

  useEffect(() => {
    if (!room) return;
    const onError = (error: Error, kind?: MediaDeviceKind) => {
      const cause = classifyMediaError(error);
      if (kind === "videoinput") setCameraError(cause);
      else if (kind === "audioinput") setMicError(cause);
      // Events without a device kind (older combined-request failures) are left
      // to the per-device toggle path, which knows exactly which device failed.
    };
    room.on(RoomEvent.MediaDevicesError, onError);
    return () => {
      room.off(RoomEvent.MediaDevicesError, onError);
    };
  }, [room]);

  const toggleCamera = useCallback(
    async (next: boolean): Promise<boolean> => {
      if (!localParticipant) return false;
      try {
        await localParticipant.setCameraEnabled(next);
        setCameraError(null);
        return true;
      } catch (err) {
        // Disabling never blocks — only surface enable failures.
        if (next) setCameraError(classifyMediaError(err));
        return false;
      }
    },
    [localParticipant],
  );

  const toggleMic = useCallback(
    async (next: boolean): Promise<boolean> => {
      if (!localParticipant) return false;
      try {
        await localParticipant.setMicrophoneEnabled(next);
        setMicError(null);
        return true;
      } catch (err) {
        if (next) setMicError(classifyMediaError(err));
        return false;
      }
    },
    [localParticipant],
  );

  return { cameraError, micError, toggleCamera, toggleMic };
}
